import type {
  CmsNewsItem,
  CmsPagePayload,
  FrontNewsListItem,
  FrontNewsPageData,
  FrontProductCenterData,
  FrontProductCenterType
} from '../api/types/cms';

/** 将列表类接口返回值规范为数组 */
export function normalizeCmsList<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) {
    return raw as T[];
  }
  if (raw && typeof raw === 'object') {
    const p = raw as CmsPagePayload<T>;
    return (p.records ?? p.list ?? p.rows ?? []) as T[];
  }
  return [];
}

/** 将分页类接口返回值规范为 { list, total } */
export function normalizeCmsPage<T>(raw: unknown): { list: T[]; total: number } {
  if (Array.isArray(raw)) {
    return { list: raw as T[], total: raw.length };
  }
  if (raw && typeof raw === 'object') {
    const p = raw as CmsPagePayload<T>;
    const list = (p.records ?? p.list ?? p.rows ?? []) as T[];
    const total = Number(p.total ?? p.totalCount ?? list.length);
    return { list, total };
  }
  return { list: [], total: 0 };
}

/** 首页接口可能直接返回 banners 数组或包在对象里 */
export function normalizeHomepage(raw: unknown): Record<string, unknown> | null {
  if (!raw) {
    return null;
  }
  if (Array.isArray(raw)) {
    return { banners: raw };
  }
  if (typeof raw === 'object') {
    return raw as Record<string, unknown>;
  }
  return null;
}

export function pickNewsList(raw: unknown): CmsNewsItem[] {
  const page = normalizeCmsPage<CmsNewsItem>(raw);
  if (page.list.length) {
    return page.list;
  }
  return normalizeCmsList<CmsNewsItem>(raw);
}

/** 规范前台产品中心数据 */
export function normalizeProductCenter(raw: unknown): FrontProductCenterData {
  if (!raw || typeof raw !== 'object') {
    return { types: [] };
  }
  const root = raw as Record<string, unknown>;
  const typesRaw = root.types ?? root.typeList;
  if (!Array.isArray(typesRaw)) {
    return { types: [] };
  }
  const types = typesRaw.map((item) => {
    const t = item as Record<string, unknown>;
    const categoriesRaw = t.categories ?? t.categoryList;
    const categories = Array.isArray(categoriesRaw)
      ? categoriesRaw.map((cat) => {
          const c = cat as Record<string, unknown>;
          const productsRaw = c.products ?? c.productList;
          return {
            id: c.id as FrontProductCenterType['id'],
            name: String(c.name ?? ''),
            products: Array.isArray(productsRaw) ? productsRaw : []
          };
        })
      : [];
    return {
      id: t.id as FrontProductCenterType['id'],
      name: String(t.name ?? ''),
      code: t.code ? String(t.code) : undefined,
      categories
    };
  });
  return { types };
}

/** 规范前台新闻分页（total/size/current/pages 可能为字符串） */
export function normalizeFrontNewsPage(raw: unknown): FrontNewsPageData {
  if (!raw || typeof raw !== 'object') {
    return { records: [], total: 0, size: 12, current: 1, pages: 1 };
  }

  const p = raw as Record<string, unknown>;
  const records = (p.records ?? p.list ?? p.rows ?? []) as FrontNewsListItem[];

  return {
    records,
    total: Number(p.total ?? records.length) || 0,
    size: Number(p.size ?? 12) || 12,
    current: Number(p.current ?? 1) || 1,
    pages: Number(p.pages ?? 1) || 1
  };
}
